/**
 * StepperContent
 *
 * Renders the step content with support for different animation types.
 * Handles the layout and transitions between steps.
 */

import React from 'react';
import { ANIMATION_CONSTANTS } from '../../../../constants/animationConstants';
import { StepActiveContext } from '../stepNavContext';
import { qaAttr } from '../../../../utils/qaAttr';
import type { StepConfig, AnimationType } from '../ExpauseStepper';

export interface StepperContentProps {
  /** Array of step configurations */
  steps: StepConfig[];

  /** Current active step index */
  currentStep: number;

  /** Animation type for step transitions */
  animationType: AnimationType;
}

export const StepperContent: React.FC<StepperContentProps> = ({
  steps,
  currentStep,
  animationType,
}) => {
  const shouldRenderStep = (index: number, keepAlive: boolean = true): boolean => {
    if (!keepAlive) {
      return Math.abs(currentStep - index) <= 1;
    }
    return true;
  };

  const getStepStyle = (index: number): React.CSSProperties => {
    const isActive = currentStep === index;

    if (animationType === 'slide') {
      // For slide: each step takes full width in a row
      return {
        minWidth: '100%',
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        flexShrink: 0,
      };
    }

    // For fade and none: absolute positioning
    const baseStyle: React.CSSProperties = {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflowY: 'auto',
    };

    if (animationType === 'none') {
      return {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden', // Step content handles its own scrolling
        display: isActive ? 'flex' : 'none',
        flexDirection: 'column' as const,
      };
    }

    // Fade animation
    return {
      ...baseStyle,
      opacity: isActive ? 1 : 0,
      pointerEvents: isActive ? 'auto' : 'none',
      transition: `opacity ${ANIMATION_CONSTANTS.duration.normal}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
    };
  };

  // Container style varies by animation type:
  // - 'none': flex column, steps use display:flex/none (normal flow)
  // - 'fade': needs position:relative for absolute children, height:100% to fill parent
  // - 'slide': needs height:100% for the slide wrapper
  const contentContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0, // Allow flex item to shrink below content size
    overflow: 'hidden', // Let individual steps handle their own scrolling
    // For fade/slide: need explicit height and position for proper containment
    ...(animationType !== 'none' && {
      position: 'relative' as const,
      height: '100%',
    }),
  };

  // For slide animation: wrapper that slides horizontally
  const slideWrapperStyle: React.CSSProperties = animationType === 'slide'
    ? {
      display: 'flex',
      flexDirection: 'row',
      height: '100%',
      width: '100%',
      transform: `translateX(-${currentStep * 100}%)`,
      transition: `transform ${ANIMATION_CONSTANTS.duration.normal}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
    }
    : {};

  return (
    <div {...qaAttr({ id: 'stepper-content', value: currentStep })} style={contentContainerStyle}>
      {animationType === 'slide' ? (
        <div style={slideWrapperStyle}>
          {steps.map((step, index) => {
            const shouldRender = shouldRenderStep(index, step.keepAlive);

            if (!shouldRender) {
              return <div key={step.id ?? index} style={{ minWidth: '100%', width: '100%' }} />;
            }

            return (
              <div key={step.id ?? index} style={getStepStyle(index)}>
                <StepActiveContext.Provider value={index === currentStep}>
                  {step.content}
                </StepActiveContext.Provider>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {steps.map((step, index) => {
            const shouldRender = shouldRenderStep(index, step.keepAlive);

            if (!shouldRender) {
              return <div key={step.id ?? index} />;
            }

            return (
              <div key={step.id ?? index} style={getStepStyle(index)}>
                <StepActiveContext.Provider value={index === currentStep}>
                  {step.content}
                </StepActiveContext.Provider>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};
